package com.parmartechnologies.storage.collection

import kotlinx.serialization.ExperimentalSerializationApi
import kotlinx.serialization.KSerializer
import kotlinx.serialization.SerializationStrategy
import kotlinx.serialization.descriptors.PolymorphicKind
import kotlinx.serialization.descriptors.SerialDescriptor
import kotlinx.serialization.encoding.AbstractEncoder
import kotlinx.serialization.encoding.CompositeEncoder
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.modules.EmptySerializersModule
import kotlinx.serialization.modules.SerializersModule

open class DatabaseCollectionHelper {
    val json = Json {
        encodeDefaults = true
        ignoreUnknownKeys = true
        isLenient = true
    }

    @OptIn(ExperimentalSerializationApi::class)
    class ListEncoder : AbstractEncoder() {
        val list: MutableList<Any?> = mutableListOf()

        override val serializersModule: SerializersModule = EmptySerializersModule()

        override fun encodeValue(value: Any) {
            list.add(value)
        }

        override fun encodeFloat(value: Float) {
            list.add(value.toString().toDouble())
        }

        override fun encodeEnum(enumDescriptor: SerialDescriptor, index: Int) {
            list.add(enumDescriptor.getElementName(index))
        }

        override fun encodeNull() {
            list.add(null)
        }

        override fun beginStructure(descriptor: SerialDescriptor): CompositeEncoder {
            val encoder = MapEncoder(descriptor.kind is PolymorphicKind.SEALED)
            list.add(encoder.map)
            return encoder
        }

        override fun beginCollection(
            descriptor: SerialDescriptor,
            collectionSize: Int
        ): CompositeEncoder {
            val encoder = ListEncoder()
            list.add(encoder.list)

            return encoder
        }
    }

    @OptIn(ExperimentalSerializationApi::class)
    class MapEncoder(val sealed: Boolean = false) : AbstractEncoder() {
        override val serializersModule: SerializersModule = EmptySerializersModule()

        val map: MutableMap<String, Any?> = mutableMapOf()

        private var _key: String? = null

        private var key: String
            get() = _key!!
            set(key) {
                _key = key
            }

        fun hasKey() = _key != null

        override fun encodeValue(value: Any) {
            map[key] = value
        }

        override fun encodeFloat(value: Float) {
            map[key] = value.toString().toDouble()
        }

        override fun encodeEnum(enumDescriptor: SerialDescriptor, index: Int) {
            map[key] = enumDescriptor.getElementName(index)
        }

        override fun encodeNull() {
            map[key] = null
        }

        override fun encodeElement(descriptor: SerialDescriptor, index: Int): Boolean {
            key = descriptor.getElementName(index)
            return true
        }

        override fun beginStructure(descriptor: SerialDescriptor): CompositeEncoder {
            if (!hasKey()) return this

            if (sealed && key == "value") {
                return this
            }

            val encoder = MapEncoder(descriptor.kind is PolymorphicKind.SEALED)
            map[key] = encoder.map
            return encoder
        }

        override fun beginCollection(
            descriptor: SerialDescriptor,
            collectionSize: Int
        ): CompositeEncoder {
            val encoder = ListEncoder()
            map[key] = encoder.list
            return encoder
        }
    }

    @OptIn(ExperimentalSerializationApi::class)
    fun <T> encodeToMap(serializer: SerializationStrategy<T>, value: T): Map<String, Any?> {
        val encoder = MapEncoder(sealed = serializer.descriptor.kind is PolymorphicKind.SEALED)
        encoder.encodeSerializableValue(serializer, value)
        val map = encoder.map
        map.put("id",map.get("_id"))
        map.remove("_id")
        return map
    }

    @OptIn(ExperimentalSerializationApi::class)
    fun <T>getCollectionName(serializer: KSerializer<T>):String{
        var name = ""
        val serialName = serializer.descriptor.serialName.split(".").last()
        if(serialName.isNotEmpty()){
            return serialName
        }else if(serializer.descriptor.serialName.isNotEmpty()){
            return serializer.descriptor.serialName
        }else{
            return "Sample collection"
        }
    }
    fun addBackTheId(data: JsonElement?): JsonObject {
        val jsonData : MutableMap<String, JsonElement>? = data?.jsonObject?.toMutableMap()
        val id = jsonData?.get("id")
        return if(jsonData != null){
            if(id != null){
                jsonData["_id"] = id
            }
            JsonObject(jsonData)
        }else{
            JsonObject(
                content = mapOf()
            )
        }
    }
    @OptIn(ExperimentalSerializationApi::class)
    fun <T> getValueForKey(key:String,value: T, serializer:KSerializer<T>): String {
        val encoder = MapEncoder(sealed = serializer.descriptor.kind is PolymorphicKind.SEALED)
        encoder.encodeSerializableValue(serializer, value)
        val map = encoder.map
        if(map.containsKey(key)){
            return map[key] as String
        }else{
            return ""
        }
    }
}